import { useAxios } from 'hooks/useAxios'
import {
  RefreshTokenRequest,
  RefreshTokenResponse,
  SendOtpResponse,
  UseSendOtpArgs,
  VerifyOtpRequest,
  VerifyOtpResponse,
} from './types'
import { useRecoilState, useSetRecoilState } from 'recoil'
import { currentTokenState, otpSendState, resetAuthState } from '../store'
import { useState } from 'react'
import { addAlertQuery } from 'services/alert'
import { currentUserState } from 'services/user'
import { AxiosError } from 'axios'
import { BaseErrorResponse } from 'shared/http/types'
import { resetRecoil } from 'recoil-nexus'
import { parseJwt } from '../../../utils/parse-jwt'
import { IS_LOCAL } from '../../../constants'

export const useSendOtp = (_?: UseSendOtpArgs) => {
  const { request } = useAxios<SendOtpResponse>({ url: `/auth/otp` })
  const [isLoading, setIsLoading] = useState(false)
  const setOtpSended = useSetRecoilState(otpSendState)
  const addAlert = useSetRecoilState(addAlertQuery)

  const sendOTP = async (phoneNumber: number) => {
    try {
      setIsLoading(true)
      const { data } = await request({ options: { params: { phone: phoneNumber } } })
      setOtpSended({ phone: phoneNumber, sent: true, otp: data.code })
    } catch (err) {
      const error = err as AxiosError<BaseErrorResponse>
      addAlert([{ message: error.response?.data?.message }])
    } finally {
      setIsLoading(false)
    }
  }

  return { isLoading, sendOTP }
}

export const useVerifyOtp = () => {
  const { request } = useAxios<VerifyOtpResponse>({ url: '/auth/validate', method: 'POST' })
  const [isLoading, setIsLoading] = useState(false)
  const [currentTokens, setCurrentTokens] = useRecoilState(currentTokenState)
  const setUser = useSetRecoilState(currentUserState)
  const addAlert = useSetRecoilState(addAlertQuery)

  const verifyOTP = async (args: VerifyOtpRequest) => {
    try {
      setIsLoading(true)
      const { data } = await request<VerifyOtpRequest>({ input: args })

      setCurrentTokens({
        access: data.accessToken,
        refresh: data.refreshToken,
      })

      // NOTE: Does not work on localhost development.
      const { uid } = parseJwt(data.accessToken)
      setUser({ id: IS_LOCAL ? '1' : uid, name: '' })
    } catch (err: unknown) {
      const error = err as AxiosError<BaseErrorResponse>
      resetRecoil(otpSendState)
      addAlert([{ message: error.response?.data?.message }])
    } finally {
      setIsLoading(false)
    }
  }

  return { isLoading, data: currentTokens, verifyOTP }
}

export const useRefreshToken = () => {
  const { request } = useAxios<RefreshTokenResponse>({ url: '/auth/refresh', method: 'POST' })

  const refreshToken = async (token: string): Promise<RefreshTokenResponse | null> => {
    const { data, status } = await request<RefreshTokenRequest>({
      input: { refreshToken: token },
      options: { validateStatus: null },
    })

    if (status >= 400) {
      resetRecoil(resetAuthState)
      throw new AxiosError()
    }

    return data
  }

  return { refreshToken }
}
